/**
 * Telemetria do pipeline — tela interna, fora do roteiro de demonstração.
 *
 * Latência e tokens saíram das telas de demo de propósito: número de delay na
 * frente do cliente assusta. Aqui eles aparecem por turno, direto do backend
 * (`GET /api/pipeline/metrics`), para calibrar modelos e argumentar custo.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import { color, font, radius } from '../theme/tokens'
import type { PipelineMetrics } from '../lib/types'

const seconds = (ms: number | null) =>
  ms === null ? '—' : `${(ms / 1000).toFixed(1).replace('.', ',')} s`

export function Debug() {
  const [metrics, setMetrics] = useState<PipelineMetrics | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    const load = () =>
      api
        .pipelineMetrics()
        .then((data) => {
          if (!alive) return
          setMetrics(data)
          setError(null)
        })
        .catch((err: unknown) => {
          if (alive) setError(err instanceof Error ? err.message : 'Falha ao ler a telemetria')
        })
    load()
    const id = window.setInterval(load, 2000)
    return () => {
      alive = false
      window.clearInterval(id)
    }
  }, [])

  const turns = metrics ? [...metrics.turns].reverse() : []

  return (
    <div
      style={{
        minHeight: '100vh',
        background: color.panel,
        color: '#fff',
        padding: '28px 24px',
        display: 'flex',
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <div style={{ width: 860, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Telemetria do pipeline · Modelo B</div>
            <div style={{ fontSize: 12, color: color.panelTextFaint, marginTop: 3 }}>
              Tela interna — não faz parte do roteiro de demonstração.
            </div>
          </div>
          <Link to="/" style={{ fontSize: 12, color: color.panelTextMuted }}>
            ← menu
          </Link>
        </div>

        {metrics ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              fontSize: 11.5,
              fontFamily: font.mono,
              color: color.panelTextMuted,
            }}
          >
            {[
              `ASR ${metrics.asr_model}`,
              `GPT ${metrics.chat_model}`,
              `TTS ${metrics.tts_model} · voz ${metrics.voice}`,
            ].map((chip) => (
              <span
                key={chip}
                style={{
                  padding: '5px 10px',
                  borderRadius: radius.pill,
                  background: 'rgba(255,255,255,.06)',
                  border: '1px solid rgba(255,255,255,.1)',
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        ) : null}

        {error ? (
          <div
            style={{
              fontSize: 12,
              color: '#ffd9cf',
              background: 'rgba(237,79,46,.12)',
              border: '1px solid rgba(237,79,46,.35)',
              borderRadius: radius.lg,
              padding: '10px 12px',
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            background: '#1b2330',
            border: '1px solid rgba(255,255,255,.09)',
            borderRadius: radius.panel,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ color: color.panelTextFaint, fontSize: 11, textAlign: 'left' }}>
                {['Chamada', 'Momento', 'GPT', 'TTS', 'Tokens (in · cache / out)', 'Ferramentas'].map(
                  (header) => (
                    <th
                      key={header}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid rgba(255,255,255,.09)',
                        fontWeight: 600,
                      }}
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {turns.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{ padding: '18px 14px', color: color.panelTextDim, fontSize: 12.5 }}
                  >
                    Sem turnos registrados ainda — faça uma chamada no Modelo B (/linha-2).
                  </td>
                </tr>
              ) : (
                turns.map((turn, index) => (
                  <tr key={`${turn.call_id}-${index}`} style={{ borderBottom: '1px solid rgba(255,255,255,.05)' }}>
                    <td style={{ padding: '8px 14px', fontFamily: font.mono, fontSize: 11.5, color: color.panelTextMuted }}>
                      {turn.call_id.slice(0, 8)}
                    </td>
                    <td style={{ padding: '8px 14px' }}>{turn.at}</td>
                    <td style={{ padding: '8px 14px' }}>{seconds(turn.chat_ms)}</td>
                    <td style={{ padding: '8px 14px' }}>{seconds(turn.tts_ms)}</td>
                    <td style={{ padding: '8px 14px' }}>
                      {turn.prompt_tokens}
                      <span style={{ color: color.panelTextDim }}> · {turn.cached_tokens ?? 0}</span>
                      {' / '}
                      {turn.completion_tokens}
                    </td>
                    <td style={{ padding: '8px 14px', color: color.panelTextMuted }}>
                      {turn.tools.length > 0 ? turn.tools.join(', ') : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 11.5, color: color.panelTextDim, lineHeight: 1.55 }}>
          O tempo de GPT inclui as idas e vindas de ferramenta do turno; o de TTS é até o
          primeiro byte de áudio (a fala toca em streaming — o resto chega enquanto toca).
          Em "cache", quantos tokens de entrada vieram do cache de prompt da OpenAI — do
          segundo turno em diante, deve se aproximar do tamanho do prompt compilado.
          A tabela guarda os últimos 200 turnos e zera quando o backend reinicia.
        </div>
      </div>
    </div>
  )
}
